import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadSectionTexts } from '../../utils/contentService';
import { getAllPhotos, deletePhoto, deletePhotosBulk } from '../../utils/photoService';
import GalleryGrid from '../gallery/GalleryGrid';
import UploadModal from '../gallery/UploadModal';
import ImageViewerModal from '../gallery/ImageViewerModal';
import AlertModal from '../common/AlertModal';
import ConfirmModal from '../common/ConfirmModal';

export default function GallerySection() {
  const { i18n } = useTranslation();
  const [texts, setTexts] = useState({});
  const [galleryTexts, setGalleryTexts] = useState({});
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('info');
  const [confirmModal, setConfirmModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const gridRef = useRef(null);

  useEffect(() => {
    setTexts(loadSectionTexts('gallery', i18n.language));
    setGalleryTexts(loadSectionTexts('galleryPage', i18n.language));
  }, [i18n.language]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const data = await getAllPhotos();
      setPhotos(data);
    } catch (error) {
      console.error('Failed to load photos', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
    setIsAdmin(localStorage.getItem('adminAuthenticated') === 'true');
  }, []);

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setViewerOpen(true);
  };

  const handleDeletePhoto = (photoId, storagePath, thumbStoragePath) => {
    setConfirmModal({
      title: 'Delete Photo?',
      message: galleryTexts.deleteConfirm || '',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await deletePhoto(photoId, storagePath, thumbStoragePath);
          setPhotos((prev) => prev.filter((p) => p.id !== photoId));
          setConfirmModal(null);
        } catch {
          setConfirmModal(null);
          setAlertMessage(galleryTexts.deleteFailed || 'Delete failed');
          setAlertType('error');
        } finally {
          setIsDeleting(false);
        }
      },
      onCancel: () => setConfirmModal(null),
    });
  };

  const handleToggleSelect = (photoId) => {
    setSelectedPhotos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
        if (!isSelectionMode) setIsSelectionMode(true);
      }
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    if (selectedPhotos.size === 0) return;
    setConfirmModal({
      title: 'Delete Multiple Photos?',
      message: (galleryTexts.bulkDeleteConfirm || '').replace('{{count}}', selectedPhotos.size),
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          const photosToDelete = photos
            .filter((p) => selectedPhotos.has(p.id))
            .map((p) => ({ id: p.id, storagePath: p.storagePath, thumbStoragePath: p.thumbStoragePath }));
          await deletePhotosBulk(photosToDelete);
          setPhotos((prev) => prev.filter((p) => !selectedPhotos.has(p.id)));
          setSelectedPhotos(new Set());
          setIsSelectionMode(false);
          setConfirmModal(null);
        } catch (error) {
          console.error(error);
          setConfirmModal(null);
          setAlertMessage(galleryTexts.bulkDeleteFailed || 'Bulk delete failed');
          setAlertType('error');
        } finally {
          setIsDeleting(false);
        }
      },
      onCancel: () => setConfirmModal(null),
    });
  };

  const handleCancelSelection = () => {
    setSelectedPhotos(new Set());
    setIsSelectionMode(false);
  };

  const handleNext = () => {
    if (currentImageIndex < photos.length - 1) setCurrentImageIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentImageIndex > 0) setCurrentImageIndex((prev) => prev - 1);
  };

  return (
    <section className="w-full pt-12 pb-16 md:pt-20 md:pb-28 lg:pt-28 lg:pb-40 bg-test">
      <div className="mx-auto max-w-[1100px] px-4">
        {/* Header */}
        <div className="text-center mb-8 lg:mb-12">
          <h2 className="text-[30px] lg:text-[45px] leading-tight lg:leading-[50px] italic font-medium font-serif text-black mb-6 lg:mb-10">
            {texts.title || ''}
          </h2>
          <div className="space-y-2 lg:space-y-4 mb-8 lg:mb-10">
            <p className="text-[13px] lg:text-2xl font-light font-serif italic text-black leading-relaxed">
              {texts.subtitle || ''}
            </p>
            <p className="text-[13px] lg:text-2xl font-light font-serif italic text-black leading-relaxed">
              {texts.subtitle2 || ''}
            </p>
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => setUploadModalOpen(true)}
              className="w-full max-w-[174px] lg:max-w-[288px] h-[46px] lg:h-16 bg-blue1 rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              <span className="text-[17px] lg:text-[23px] font-normal font-serif italic text-white text-center">
                {texts.uploadButton || ''}
              </span>
            </button>
          </div>
        </div>

        {/* Admin Bulk Selection Bar */}
        {isAdmin && isSelectionMode && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4 flex justify-center items-center gap-6 animate-slide-up">
            <span className="text-lg font-['IBM_Plex_Serif'] font-medium">
              {selectedPhotos.size} {galleryTexts.selected || ''}
            </span>
            <button
              onClick={handleCancelSelection}
              className="px-6 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 font-['IBM_Plex_Serif'] italic transition-all duration-300 shadow-sm"
            >
              {galleryTexts.cancel || ''}
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-['IBM_Plex_Serif'] italic transition-all duration-300 shadow-sm"
            >
              {galleryTexts.deleteSelected || ''}
            </button>
          </div>
        )}

        {/* Photo Grid */}
        <div ref={gridRef} className="pb-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue"></div>
            </div>
          ) : (
            <GalleryGrid
              photos={photos}
              onImageClick={handleImageClick}
              isAdmin={isAdmin}
              onDelete={handleDeletePhoto}
              isSelectionMode={isSelectionMode}
              selectedPhotos={selectedPhotos}
              onToggleSelect={handleToggleSelect}
              noImagesText={galleryTexts.noImages}
            />
          )}
        </div>
      </div>

      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadSuccess={fetchPhotos}
      />

      <ImageViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        image={photos[currentImageIndex]}
        prevImage={photos[currentImageIndex - 1]}
        nextImage={photos[currentImageIndex + 1]}
        onNext={handleNext}
        onPrev={handlePrev}
        hasNext={currentImageIndex < photos.length - 1}
        hasPrev={currentImageIndex > 0}
      />

      {alertMessage && (
        <AlertModal
          message={alertMessage}
          type={alertType}
          onClose={() => setAlertMessage(null)}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isDeleting}
        />
      )}
    </section>
  );
}
